import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import AuthLayoutWrapper from '@/components/auth/AuthLayoutWrapper';
import { useSubcontractorProfile } from '@/contexts/SubcontractorProfileContext';
import { supabase } from '@/lib/supabaseClient';

const AuthStateListener = ({ children }) => {
  const { profile, profileLoading, isHydrated } = useSubcontractorProfile();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(() => {
      // Profile context handles the actual profile refresh
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!isHydrated || profileLoading) return;

    const host = window.location.hostname.toLowerCase();

    const isDriverSubdomain =
      host.includes('drivers.parcelink.org') ||
      host.includes('drivers-local.parcelink.org') ||
      host.startsWith('drivers.');

    const isAccountingSubdomain =
      host.includes('accounting.parcelink.org') ||
      host.includes('accounting-local.parcelink.org') ||
      host.startsWith('accounting.');

    const isReceiptsSubdomain =
      host.includes('receipts.parcelink.org') ||
      host.includes('receipts-local.parcelink.org') ||
      host.startsWith('receipts.');

    const isPartnersSubdomain =
      host.includes('partners.parcelink.org') ||
      host.includes('partners-local.parcelink.org') ||
      host.startsWith('partners.');

    // runmark.co.uk is the control plane, where other operators buy a portal.
    // Nobody arriving here has an account yet — that is the whole point — so
    // this listener must not treat its pages as somewhere a logged-out visitor
    // has wandered off to and bounce them back.
    const isControlPlane =
      host === 'runmark.co.uk' ||
      host === 'www.runmark.co.uk' ||
      host === 'runmark.localhost';

    const pathname = location.pathname;

    // The control plane has its own route table (App.jsx) and none of the
    // portal's destinations: no /admin/dashboard, /subcontractor/dashboard or
    // /accountant. Sending a signed-in user to one hit its catch-all, which
    // sent them back to /, which sent them on again — a replaceState loop the
    // browser killed with a SecurityError, so nobody signed in could use the
    // site. Who may use /control is decided by is_platform_owner() and the page
    // itself, not here: a signed-in user on the sign-in page (or the auth
    // callback, which waits for this listener to move it on) goes to the
    // console, and every other control-plane page is left where it is.
    if (isControlPlane) {
      if (profile && (pathname === '/login' || pathname === '/admin/login' || pathname.startsWith('/auth/'))) {
        navigate('/control', { replace: true });
      }
      return;
    }

    // A partner's introduced driver opens a personal link (stage 4b). It is
    // the same page signed in or out, so nobody is sent anywhere from it.
    if (pathname.startsWith('/introduced/')) return;

    const generalAuthRoutes = [
      '/',
      '/admin/login',
      '/subcontractor/login',
      // The accountant's own sign-in page. Without it here a signed-out
      // accountant asking for a magic link was redirected to the CREW login
      // instead — the one page they must be able to reach, unreachable. They
      // could only ever get in from an invite email, and never again after it
      // expired. (An accountant who is already signed in is still pinned to
      // /accountant below, so this only opens the door, never the portal.)
      '/accountant/login',
      // The partner portal's sign-in page (stage 4): open to a signed-out
      // Partner Admin; a signed-in one is pinned to /partner-portal below.
      '/partner-portal/login',
      '/magic-link-sent',
      '/magic-link-confirmation',
      '/auth/callback',
    ];

    const accountingAuthRoutes = [
      '/',
      '/admin/login',
      '/magic-link-sent',
      '/magic-link-confirmation',
      '/auth/callback',
    ];

    const driverAuthRoutes = [
      '/',
      '/auth/callback',
      '/magic-link-sent',
      '/magic-link-confirmation',
      '/management',
    ];

    // Public, no-login marketing/recruitment pages (must never redirect to login).
    const isPublicMarketingRoute =
      pathname.startsWith('/jobs') ||
      pathname.startsWith('/blog') ||
      // /partner-portal is a signed-in area, not the partners marketing site.
      (pathname.startsWith('/partner') && !pathname.startsWith('/partner-portal')) ||
      pathname.startsWith('/proof') ||
      pathname.startsWith('/about') ||
      pathname.startsWith('/privacy') ||
      pathname.startsWith('/terms') ||
      pathname.startsWith('/gdpr') ||
      pathname.startsWith('/disclaimer') ||
      pathname.startsWith('/software') ||
      // The old preview address of the apex home; it forwards to / (1 Nov).
      pathname === '/preview/home' ||
      // Control-plane signup: the buyer has no account, and /welcome is watched
      // while provisioning runs — bouncing either to a login would break the
      // one flow that is supposed to work for a total stranger.
      (isControlPlane && (pathname === '/' ||
                          // The configurator is the step BEFORE signup and is
                          // as public as the price list it renders.
                          pathname.startsWith('/build') ||
                          pathname.startsWith('/signup') ||
                          pathname.startsWith('/welcome') ||
                          // /control is NOT public — it is gated by
                          // is_platform_owner() at the data layer and by the
                          // page itself. It is listed here only so the route
                          // renders its own "sign in" or "not your console"
                          // answer instead of being bounced to the marketing
                          // page, which told a signed-out owner nothing.
                          pathname.startsWith('/control') ||
                          pathname.startsWith('/login')));

    // The NDA-gated demo entry is public by design: attendees arrive logged out
    // (a scanned QR, or the host's shared audience link) and are signed in BY
    // the gate after signing the agreement. The crew login page no longer
    // advertises it — see hasDemoQrInvite() in src/lib/demo.js.
    const isDemoGateRoute = pathname === '/demo' || pathname.startsWith('/demo/');

    const isGeneralAuthRoute =
      pathname.startsWith('/auth/') ||
      pathname.startsWith('/mcl/') ||
      pathname.startsWith('/onboarding/') ||
      pathname.startsWith('/journey') ||
      isPublicMarketingRoute ||
      isDemoGateRoute ||
      generalAuthRoutes.includes(pathname);

    const isAccountingAuthRoute =
      pathname.startsWith('/auth/') || accountingAuthRoutes.includes(pathname);

    const isDriverAuthRoute =
      pathname.startsWith('/auth/') ||
      pathname.startsWith('/journey') ||
      isPublicMarketingRoute ||
      driverAuthRoutes.includes(pathname);

    const receiptsAuthRoutes = [
      '/admin/login',
      '/auth/callback',
    ];

    const isReceiptsAuthRoute =
      pathname.startsWith('/auth/') || receiptsAuthRoutes.includes(pathname);

    const isAuthRoute = isPartnersSubdomain
      ? true // the partners (client) site is entirely public — never redirect to login
      : isAccountingSubdomain
      ? isAccountingAuthRoute
      : isDriverSubdomain
        ? isDriverAuthRoute
        : isReceiptsSubdomain
          ? isReceiptsAuthRoute
          : isGeneralAuthRoute;

    const searchParams = new URLSearchParams(location.search);
    const shouldBypassAdminRedirect = searchParams.get('view_portal') === 'true';

    if (profile) {
      if (profile.role === 'admin') {
        if (isAuthRoute && !shouldBypassAdminRedirect) {
          let target = '/admin/dashboard';

          if (isDriverSubdomain) {
            target = '/management';
          }

          if (isAccountingSubdomain) {
            target = '/dashboard';
          }

          if (isReceiptsSubdomain) {
            target = '/';
          }

          if (pathname !== target) {
            navigate(target, { replace: true });
          }
        }
      } else if (profile.role === 'subcontractor') {
        if (isAuthRoute && !isAccountingSubdomain && !isDriverSubdomain) {
          navigate('/subcontractor/dashboard', { replace: true });
        }
      } else if (profile.role === 'accountant') {
        // Accountants live only at /accountant — pin them there.
        if (!pathname.startsWith('/accountant') || isAuthRoute) {
          if (pathname !== '/accountant') navigate('/accountant', { replace: true });
        }
      } else if (profile.role === 'partner') {
        // Partner Admins live only in the partner portal (stage 4).
        if (!pathname.startsWith('/partner-portal') || isAuthRoute) {
          if (pathname !== '/partner-portal') navigate('/partner-portal', { replace: true });
        }
      }
    } else {
      if (!isAuthRoute) {
        let target = '/subcontractor/login';

        if (isAccountingSubdomain) {
          target = '/';
        } else if (isReceiptsSubdomain) {
          target = '/admin/login';
        } else if (pathname.startsWith('/admin/')) {
          target = '/admin/login';
        } else if (pathname.startsWith('/partner-portal')) {
          // A partner signed out, e.g. the home-screen app (its own sign-in on an
          // iPhone) opening /partner-portal: their own sign-in, never the crew's
          // (owner, 14 Oct 2026).
          target = '/partner-portal/login';
        }

        navigate(target, { replace: true });
      }
    }
  }, [profile, profileLoading, isHydrated, location.pathname, location.search, navigate]);

  if (!isHydrated || (profileLoading && !profile)) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-900 dark:bg-slate-950">
        <Loader2 className="h-16 w-16 text-blue-500 animate-spin" />
        <p className="text-gray-300 dark:text-gray-400 mt-4 text-lg font-medium tracking-tight">
          Securing your session...
        </p>
      </div>
    );
  }

  return (
    <AuthLayoutWrapper session={profile ? { user: profile } : null}>
      {children}
    </AuthLayoutWrapper>
  );
};

export default AuthStateListener;